/** Configuration for the decorative geometric frame rendered around card components. */
export interface GeometricConfig {
  width: number;
  height: number;
  strokeWidth: number;
  radius: number;

  // Horizontal Configuration
  hGap: number;
  hLine: boolean;
  hMode: 'center' | 'edge';
  hSize: number; // center length if mode='center', corner length if mode='edge'

  // Vertical Configuration
  vGap: number;
  vLine: boolean;
  vMode: 'center' | 'edge';
  vSize: number;
}

/** Generates SVG path strings for a rounded-corner frame with optional center gap lines. */
export const generateFramePaths = (config: GeometricConfig) => {
  const {
    width,
    height,
    strokeWidth,
    radius,
    hGap,
    hLine,
    hMode,
    hSize,
    vGap,
    vLine,
    vMode,
    vSize,
  } = config;

  const inset = strokeWidth / 2;
  const top = inset;
  const bottom = height - inset;
  const left = inset;
  const right = width - inset;

  // Clamp radius to avoid artifacts on small containers
  const clampedRadius = Math.min(radius, width / 2, height / 2);
  const arcR = Math.max(0, clampedRadius - inset);

  let xCornerLeftEnd = 0;
  let xCenterLineStart = 0;
  let xCenterLineEnd = 0;
  let xCornerRightStart = 0;

  if (hMode === 'center') {
    // Gap and Line are defined relative to the center
    const midX = width / 2;
    const halfCenter = hSize / 2;
    xCenterLineStart = midX - halfCenter;
    xCenterLineEnd = midX + halfCenter;
    xCornerLeftEnd = xCenterLineStart - hGap;
    xCornerRightStart = xCenterLineEnd + hGap;
  } else {
    // Gap and Line are defined relative to the edges (Corner Length)
    xCornerLeftEnd = left + hSize;
    xCornerRightStart = right - hSize;
    xCenterLineStart = xCornerLeftEnd + hGap;
    xCenterLineEnd = xCornerRightStart - hGap;
  }

  let yCornerTopEnd = 0;
  let yCenterLineStart = 0;
  let yCenterLineEnd = 0;
  let yCornerBottomStart = 0;

  if (vMode === 'center') {
    const midY = height / 2;
    const halfCenter = vSize / 2;
    yCenterLineStart = midY - halfCenter;
    yCenterLineEnd = midY + halfCenter;
    yCornerTopEnd = yCenterLineStart - vGap;
    yCornerBottomStart = yCenterLineEnd + vGap;
  } else {
    yCornerTopEnd = top + vSize;
    yCornerBottomStart = bottom - vSize;
    yCenterLineStart = yCornerTopEnd + vGap;
    yCenterLineEnd = yCornerBottomStart - vGap;
  }

  const topLeft = `M ${left} ${yCornerTopEnd} V ${top + arcR} A ${arcR} ${arcR} 0 0 1 ${left + arcR} ${top} H ${xCornerLeftEnd}`;
  const topRight = `M ${xCornerRightStart} ${top} H ${right - arcR} A ${arcR} ${arcR} 0 0 1 ${right} ${top + arcR} V ${yCornerTopEnd}`;
  const bottomRight = `M ${right} ${yCornerBottomStart} V ${bottom - arcR} A ${arcR} ${arcR} 0 0 1 ${right - arcR} ${bottom} H ${xCornerRightStart}`;
  const bottomLeft = `M ${xCornerLeftEnd} ${bottom} H ${left + arcR} A ${arcR} ${arcR} 0 0 1 ${left} ${bottom - arcR} V ${yCornerBottomStart}`;

  let topCenter = '';
  let bottomCenter = '';
  if (hLine) {
    topCenter = `M ${xCenterLineStart} ${top} H ${xCenterLineEnd}`;
    bottomCenter = `M ${xCenterLineStart} ${bottom} H ${xCenterLineEnd}`;
  }

  let leftCenter = '';
  let rightCenter = '';
  if (vLine) {
    leftCenter = `M ${left} ${yCenterLineStart} V ${yCenterLineEnd}`;
    rightCenter = `M ${right} ${yCenterLineStart} V ${yCenterLineEnd}`;
  }

  return {
    topLeft,
    topRight,
    bottomRight,
    bottomLeft,
    topCenter,
    bottomCenter,
    leftCenter,
    rightCenter,
  };
};
